"use client";

import { Loader2, CheckCircle2, Sparkles, X } from "lucide-react";

export interface AiStep {
  label: string;
  detail?: string;
}

interface AiProgressOverlayProps {
  /** Steps to display */
  steps: AiStep[];
  /** Index of the currently active step (0-based) */
  currentStep: number;
  /** Whether to render as a full-screen overlay or inline */
  mode?: "overlay" | "inline";
  /** Title shown above the steps */
  title?: string;
  /** Optional cancel action for long-running AI operations */
  onCancel?: () => void;
  cancelLabel?: string;
}

/**
 * Reusable step-by-step progress indicator for AI operations.
 * Use for: AI suggest fill, document generation, extraction, etc.
 */
export function AiProgressOverlay({
  steps,
  currentStep,
  mode = "overlay",
  title,
  onCancel,
  cancelLabel = "Cancel",
}: AiProgressOverlayProps) {
  const content = (
    <div className="relative flex flex-col items-center gap-4">
      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className="absolute -right-2 -top-2 inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-surface text-muted shadow-sm hover:bg-surface-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
          aria-label={cancelLabel}
        >
          <X className="h-4 w-4" />
        </button>
      )}
      {title && (
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="h-5 w-5 text-accent" />
          <span className="text-sm font-semibold text-accent-dark">{title}</span>
        </div>
      )}
      <div className="w-full max-w-xs space-y-2">
        {steps.map((step, i) => {
          const isActive = i === currentStep;
          const isComplete = i < currentStep;
          const isPending = i > currentStep;

          return (
            <div
              key={i}
              className={`flex items-center gap-3 rounded-lg px-3 py-1.5 transition-all duration-300 ${
                isActive ? "bg-accent-light" : ""
              } ${isPending ? "opacity-35" : ""}`}
            >
              <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors ${
                isComplete ? "bg-done-bg text-done" :
                isActive ? "bg-accent-light text-brand-700" :
                "bg-surface-muted text-text-tertiary"
              }`}>
                {isComplete ? (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                ) : isActive ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <div className="h-1.5 w-1.5 rounded-full bg-current" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className={`text-xs font-medium ${
                  isComplete ? "text-done" :
                  isActive ? "text-brand-700" :
                  "text-text-tertiary"
                }`}>
                  {step.label}
                </p>
                {isActive && step.detail && (
                  <p className="text-[11px] text-brand-700 truncate">{step.detail}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className="mt-1 inline-flex min-h-10 items-center justify-center rounded-lg border border-border bg-surface px-4 py-2 text-sm font-semibold text-foreground hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-accent"
        >
          {cancelLabel}
        </button>
      )}
    </div>
  );

  if (mode === "inline") {
    return content;
  }

  return (
    // The backdrop INTENTIONALLY blocks clicks on the form underneath. While
    // the AI is filling fields (the only context this overlay is rendered
    // in), the user must not be able to type into a field the AI is about to
    // overwrite — and they must not navigate Back/Next mid-prefill. The
    // designed exit is the explicit Cancel/Skip control inside the inner
    // card.
    //
    // NOTE: do NOT add pointer-events-none here without reading the comment
    // above. The first version of this fix (#559) did exactly that and it
    // turned out to be a real UX regression: users could type into the
    // fields under the scrim while AI was about to overwrite them. The
    // ACTUAL responsiveness problems the PostHog rage-click reports
    // surfaced were the pre-hydration dead window (now addressed by #561,
    // #562, #568) and the missing button-press feedback on Back/Next (now
    // addressed by wizard-nav.tsx active:scale + spinner). Letting the
    // backdrop block input is the right behaviour for AI-editing flows.
    <div className="fixed inset-0 bg-white/70 backdrop-blur-[2px] flex items-center justify-center z-50 p-4">
      <div className="rounded-2xl border border-border bg-surface px-6 py-5 shadow-xl">
        {content}
      </div>
    </div>
  );
}

/** Pre-built step sets for common operations */
export const AI_SUGGEST_STEPS: AiStep[] = [
  { label: "Analysing project details" },
  { label: "Creating suggestions", detail: "Using AI to fill relevant fields..." },
  { label: "Applying to form" },
];

export const AI_GENERATE_STEPS: AiStep[] = [
  { label: "Preparing document data" },
  { label: "Building with AI", detail: "Creating your document..." },
  { label: "Building PDF" },
  { label: "Saving document" },
];

/** Shared overlay used by wizards while AI is suggesting field values */
export function SuggestingOverlay() {
  return (
    // INTENTIONALLY blocks clicks on the section underneath: the AI is
    // filling these exact fields and any user input would be silently
    // overwritten by the AI response. See AiProgressOverlay's comment for
    // the full reasoning on why the earlier pointer-events-none version
    // was wrong.
    <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] rounded-lg flex items-center justify-center z-10">
      <AiProgressOverlay steps={AI_SUGGEST_STEPS} currentStep={1} mode="inline" title="AI Suggest" />
    </div>
  );
}
